/** NotificationContext.js:
 * Provides a global management layer for notifications using React Context.
 * Synchronizes notification state between Firestore listeners and UI components.
 */

import React, {createContext, useContext, useState} from "react";

// Create the Notifications Context
const NotificationsContext = createContext();

// Context Provider that wraps the app and provides notification state
export const NotificationsProvider = ({children}) => {
    // Local state for all notifications
    const [notifications, setNotifications] = useState([]);

    // Add a new notification to the state
    const addNotification = (notification) => {
        setNotifications((prev) => {
            const exists = prev.some((n) => n.id === notification.id);
            if (exists) return prev;
            return [...prev, notification];
        });
    };

    const removeNotification = (id) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    // Mark all notifications as read
    const markAllAsRead = () => {
        setNotifications((prev) =>
            prev.map((n) => ({...n, isRead: true}))
        );
    };

    // Marking a specific notification as read in state
    const markNotificationAsReadInContext = (id) => {
        setNotifications((prev) =>
            prev.map((n) => n.id === id ? {...n, isRead: true} : n)
        );
    };

    // Calculate how many notifications are still unread
    const unreadCount = notifications.filter((n) => !n.isRead).length;

    // Update a notification in the state
    const updateNotification = (notification) => {
        setNotifications((prev) =>
            prev.map((n) => n.id === notification.id ? { ...n, ...notification } : n)
        );
    };

    // Provide context values to children components
    return (
        <NotificationsContext.Provider
            value={{
                notifications,     // Array of all notifications
                addNotification,   // Function to add a new notification
                updateNotification, // Function to update a notification
                markAllAsRead,     // Function to mark all as read
                unreadCount,        // Number of unread notifications
                removeNotification,
                markNotificationAsReadInContext,
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};

// Custom hook to use the NotificationsContext in any component
export const useNotifications = () => useContext(NotificationsContext);